'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["aria-label", "children", "className", "descriptiveLabel", "easing", "hasLabel", "min", "max", "style", "use", "value", "valueLabel", "wrapperClassName"];
import I18n from 'I18n';
import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import classNames from 'classnames';
import styled from 'styled-components';
import { CALYPSO, CANDY_APPLE, KOALA, MARIGOLD, OLAF, OZ, SORBET } from 'HubStyleTokens/colors';
import { WEB_FONT_DEMI_BOLD_WEIGHT } from 'HubStyleTokens/misc';
import { BUTTON_RADIUS, DISTANCE_MEASUREMENT_EXTRA_SMALL, MICROCOPY_SIZE, FORM_COPY_SIZE } from 'HubStyleTokens/sizes';
import memoizeOne from 'react-utils/memoizeOne';
import { uniqueId } from '../utils/underscore';
import devLogger from 'react-utils/devLogger';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ProgressWrapper = styled.div.withConfig({
  displayName: "UIProgress__ProgressWrapper",
  componentId: "sc-1ji9pdt-0"
})(["background-color:", ";border-radius:", ";height:16px;overflow:hidden;"], ({
  use
}) => use === 'danger' ? CANDY_APPLE : KOALA, BUTTON_RADIUS);
const ProgressBar = styled.div.withConfig({
  displayName: "UIProgress__ProgressBar",
  componentId: "sc-1ji9pdt-1"
})(["background-image:linear-gradient( 89deg,", " 0%,", " 100% );transition:width 0.6s ", ";border-radius:", ";border-right:", ";color:", ";font-size:", ";height:100%;line-height:16px;text-align:center;"], ({
  use
}) => use === 'success' ? OZ : SORBET, ({
  use
}) => use === 'success' ? CALYPSO : MARIGOLD, ({
  easing
}) => easing, ({
  use
}) => use !== 'danger' && BUTTON_RADIUS, ({
  use
}) => use === 'danger' && `3px ${OLAF} solid`, OLAF, MICROCOPY_SIZE);
const ProgressAndLabelWrapper = styled.div.withConfig({
  displayName: "UIProgress__ProgressAndLabelWrapper",
  componentId: "sc-1ji9pdt-2"
})(["margin-bottom:16px;"]);
const StackedLabelWrapper = styled.div.withConfig({
  displayName: "UIProgress__StackedLabelWrapper",
  componentId: "sc-1ji9pdt-3"
})(["margin-bottom:", ";display:flex;justify-content:space-between;"], DISTANCE_MEASUREMENT_EXTRA_SMALL);
const DescriptiveLabel = styled.div.withConfig({
  displayName: "UIProgress__DescriptiveLabel",
  componentId: "sc-1ji9pdt-4"
})(["font-size:", ";"], FORM_COPY_SIZE);
const ValueLabel = styled.div.withConfig({
  displayName: "UIProgress__ValueLabel",
  componentId: "sc-1ji9pdt-5"
})(["font-size:", ";font-weight:", ";"], FORM_COPY_SIZE, WEB_FONT_DEMI_BOLD_WEIGHT);
const getStyle = (style, rawPercentage) => Object.assign({}, style, {
  width: `${rawPercentage * 100}%`
});
class UIProgress extends PureComponent {
  constructor(props) {
    super(props);
    this._getStyle = memoizeOne(getStyle);
    this._labelId = `UIProgress-label-${uniqueId()}`;
  }
  render() {
    const _this$props = this.props,
      {
        'aria-label': ariaLabel,
        children,
        className,
        descriptiveLabel,
        easing,
        hasLabel,
        min,
        max,
        style,
        use,
        value,
        valueLabel,
        wrapperClassName
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const rawPercentage = Math.min((value - min) / (max - min), 1);
    const formattedPercentage = I18n.formatPercentage(rawPercentage * 100);
    let renderedLabel;
    if (children != null) {
      renderedLabel = children;
    } else if (hasLabel) {
      renderedLabel = /*#__PURE__*/_jsx("span", {
        children: formattedPercentage
      });
    } else {
      renderedLabel = /*#__PURE__*/_jsx("span", {
        className: "sr-only",
        children: I18n.text('ui.progress.percentComplete', {
          percent: formattedPercentage
        })
      });
    }
    const ariaObject = {};
    if (descriptiveLabel) {
      ariaObject['aria-labelledby'] = this._labelId;
    } else if (ariaLabel) {
      ariaObject['aria-label'] = ariaLabel;
    } else {
      devLogger.warn({
        message: 'UIProgress: Use a descriptiveLabel prop when using this component to meet accessibility standards. If one is not needed in your design, use an aria-label to indicate the content of the UIProgress instead.'
      });
    }
    const stackedLabel = valueLabel != null || descriptiveLabel != null ? /*#__PURE__*/_jsxs(StackedLabelWrapper, {
      children: [/*#__PURE__*/_jsx(DescriptiveLabel, {
        id: this._labelId,
        children: descriptiveLabel
      }), /*#__PURE__*/_jsx(ValueLabel, {
        children: valueLabel
      })]
    }) : null;
    return /*#__PURE__*/_jsxs(ProgressAndLabelWrapper, {
      className: wrapperClassName,
      children: [stackedLabel, /*#__PURE__*/_jsx(ProgressWrapper, Object.assign({}, rest, {
        className: classNames('private-progress', className),
        use: use,
        children: /*#__PURE__*/_jsx(ProgressBar, Object.assign({
          "data-value": value,
          "aria-valuemax": max,
          "aria-valuemin": min,
          "aria-valuenow": value,
          "aria-live": "polite",
          className: "private-progress__bar",
          easing: easing,
          role: "progressbar",
          style: this._getStyle(style, rawPercentage),
          use: use
        }, ariaObject, {
          children: renderedLabel
        }))
      }))]
    });
  }
}
UIProgress.displayName = 'UIProgress';
UIProgress.defaultProps = {
  easing: 'ease',
  hasLabel: false,
  min: 0,
  max: 100,
  use: 'success',
  value: 0
};
UIProgress.propTypes = {
  'aria-label': PropTypes.string,
  children: PropTypes.node,
  descriptiveLabel: PropTypes.node,
  easing: PropTypes.string.isRequired,
  hasLabel: PropTypes.bool.isRequired,
  max: PropTypes.number.isRequired,
  min: PropTypes.number.isRequired,
  use: PropTypes.oneOf(['success', 'danger', 'warning']).isRequired,
  value: PropTypes.number.isRequired,
  valueLabel: PropTypes.node,
  wrapperClassName: PropTypes.string
};
export default UIProgress;